'use client';

import type { ActiveBanner, BannerSlot as BannerSlotKey } from '@sazeo/sdk/banners';
import { useBannerForSlot } from '@sazeo/sdk/banners';
import { X } from 'lucide-react';
import { useState } from 'react';

export interface BannerSlotProps {
  slot: BannerSlotKey;
  /** Layout variant. `inline` is in-flow with margin; `strip` is full-bleed flush. */
  layout?: 'inline' | 'strip';
  className?: string;
}

const VARIANT_BG: Record<ActiveBanner['variant'], string> = {
  promo: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  upgrade: 'linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%)',
  system: 'linear-gradient(135deg, #c2410c 0%, #9a3412 100%)',
  announcement: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
};

/**
 * Renders the active banner for a slot. Returns null when no banner is
 * enabled / in-period. Banner content comes from the public banners-active
 * Edge Function (queried via @sazeo/sdk → useBannerForSlot).
 */
export function BannerSlot({ slot, layout = 'inline', className }: BannerSlotProps) {
  const query = useBannerForSlot(slot);
  const [dismissed, setDismissed] = useState(false);

  if (query.isLoading || query.isError) return null;
  const banner = query.data;
  if (!banner || dismissed) return null;

  const flush = layout === 'strip';

  return (
    <section
      data-banner-slot={slot}
      data-banner-id={banner.id}
      data-banner-variant={banner.variant}
      aria-label={banner.title}
      className={className}
      style={{
        background: VARIANT_BG[banner.variant],
        color: '#ffffff',
        padding: '12px 16px',
        margin: flush ? 0 : '0 0 12px 0',
        borderRadius: flush ? 0 : 8,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        boxShadow: flush ? 'none' : '0 4px 12px rgba(15, 23, 42, 0.18)',
        fontSize: 13,
      }}
    >
      {banner.image_url ? (
        <img
          src={banner.image_url}
          alt=""
          aria-hidden
          style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
        />
      ) : null}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{banner.title}</div>
        {banner.body ? (
          <div style={{ marginTop: 2, fontSize: 12, opacity: 0.85, lineHeight: 1.4 }}>
            {banner.body}
          </div>
        ) : null}
      </div>
      {banner.cta_url && banner.cta_label ? (
        <a
          href={banner.cta_url}
          rel="noopener noreferrer"
          style={{
            background: '#ffffff',
            color: '#1e3a8a',
            padding: '6px 14px',
            borderRadius: 5,
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            flexShrink: 0,
          }}
        >
          {banner.cta_label}
        </a>
      ) : null}
      {banner.dismissible ? (
        <button
          type="button"
          aria-label="Dismiss banner"
          onClick={() => setDismissed(true)}
          style={{
            background: 'transparent',
            border: 0,
            color: '#ffffff',
            opacity: 0.7,
            cursor: 'pointer',
            padding: 4,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <X size={16} aria-hidden />
        </button>
      ) : null}
    </section>
  );
}
